"use client"

import { CheckCircleSolid, XMark } from "@medusajs/icons"
import { StoreCart, StorePrice } from "@medusajs/types"
import { Button, clx } from "@medusajs/ui"
import { formatAmount } from "@/modules/common/components/amount-cell"
import LocalizedClientLink from "@/modules/common/components/localized-client-link"
import { StoreFreeShippingPrice } from "@/types/shipping-option/http"
import { useEffect, useRef, useState } from "react"

export default function FreeShippingPriceNudge({
  variant = "inline",
  cart,
  freeShippingPrices,
}: {
  variant?: "popup" | "inline"
  cart: StoreCart
  freeShippingPrices: StoreFreeShippingPrice[]
}) {
  if (!cart || !freeShippingPrices?.length) {
    return
  }

  // For the starter, we are only going to focus on a singular shipping option
  const freeShippingPrice =
    freeShippingPrices[0] as unknown as StoreFreeShippingPrice

  if (!freeShippingPrice) {
    return
  }

  if (variant === "popup") {
    return <FreeShippingPopup cart={cart} price={freeShippingPrice} />
  } else {
    return <FreeShippingInline cart={cart} price={freeShippingPrice} />
  }
}

function FreeShippingInline({
  cart,
  price,
}: {
  cart: StoreCart
  price: StorePrice & {
    target_reached: boolean
    target_remaining: number
    remaining_percentage: number
  }
}) {
  const [displayWidth, setDisplayWidth] = useState(0)
  const trackRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = trackRef.current
    if (!el) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          const t = setTimeout(() => setDisplayWidth(price.remaining_percentage), 120)
          observer.disconnect()
          return () => clearTimeout(t)
        }
      },
      { threshold: 0.5 }
    )

    observer.observe(el)
    return () => observer.disconnect()
  }, [price.remaining_percentage])

  return (
    <div className={clx(
      "rounded-xl border px-4 py-3",
      price.target_reached
        ? "bg-emerald-50 border-emerald-200"
        : "bg-amber-50 border-amber-200"
    )}>
      <div className="space-y-2.5">
        {/* Labels */}
        <div className="flex items-center justify-between gap-2">
          {price.target_reached ? (
            <div className="flex items-center gap-1.5 text-sm font-semibold text-emerald-700">
              <CheckCircleSolid className="h-4 w-4 text-emerald-500" />
              Brezplačna dostava odblokirana!
            </div>
          ) : (
            <span className="text-sm font-light italic tracking-wide text-amber-900/70 font-serif">
              Odklenite brezplačno dostavo
            </span>
          )}

          <div className={clx("text-sm font-medium text-amber-700 whitespace-nowrap", {
            "opacity-0 pointer-events-none": price.target_reached,
          })}>
            še{" "}
            <span className="font-bold text-amber-900">
              {formatAmount(price.target_remaining, cart.currency_code)}
            </span>
          </div>
        </div>

        {/* Progress track — flat, square-ended, animated fill */}
        <div ref={trackRef} className="relative h-2 w-full rounded-sm overflow-visible bg-amber-200/50">
          <div
            className={clx(
              "h-full rounded-sm transition-all duration-700 ease-out",
              price.target_reached ? "bg-emerald-500" : "bg-amber-500"
            )}
            style={{
              width: `${displayWidth}%`,
              boxShadow: price.target_reached
                ? "0 0 8px 2px rgba(52,211,153,0.55)"
                : "0 0 10px 2px rgba(245,158,11,0.5)",
            }}
          />
        </div>
      </div>
    </div>
  )
}

function FreeShippingPopup({
  cart,
  price,
}: {
  cart: StoreCart
  price: StorePrice & {
    target_reached: boolean
    target_remaining: number
    remaining_percentage: number
  }
}) {
  const [isClosed, setIsClosed] = useState(false)

  if (cart.items?.length === 0) {
    return
  }

  return (
    <div
      className={clx(
        "fixed bottom-5 right-5 flex flex-col items-end gap-2 transition-all duration-500 ease-in-out",
        {
          "opacity-0 invisible delay-1000": price.target_reached,
          "opacity-0 invisible": isClosed,
          "opacity-100 visible": !price.target_reached && !isClosed,
        }
      )}
    >
      <div>
        <Button
          className="rounded-full bg-neutral-900 shadow-none outline-none border-none text-[15px] p-2"
          onClick={() => setIsClosed(true)}
        >
          <XMark />
        </Button>
      </div>

      <div className="w-[400px] bg-black text-white p-6 rounded-lg ">
        <div className="pb-4">
          <div className="space-y-3">
            <div className="flex justify-between text-[15px] text-neutral-400">
              <div>
                {price.target_reached ? (
                  <div className="flex items-center gap-1.5">
                    <CheckCircleSolid className="text-green-500 inline-block" />{" "}
                    Brezplačna dostava!
                  </div>
                ) : (
                  `Pridobite brezplačno dostavo`
                )}
              </div>

              <div
                className={clx("visible", {
                  "opacity-0 invisible": price.target_reached,
                })}
              >
                samo{" "}
                <span className="text-white">
                  {formatAmount(price.target_remaining, cart.currency_code)}
                </span>{" "}
                več
              </div>
            </div>
            <div className="flex justify-between gap-1">
              <div
                className={clx(
                  "bg-gradient-to-r from-zinc-400 to-zinc-500 h-1 rounded-full max-w-full duration-500 ease-in-out",
                  {
                    "from-green-400 to-green-500": price.target_reached,
                  }
                )}
                style={{ width: `${price.remaining_percentage}%` }}
              ></div>
              <div className="bg-neutral-600 h-1 rounded-full w-fit flex-grow"></div>
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-y-2">
          <LocalizedClientLink href="/search">
            <Button className="w-full h-10 rounded-full shadow-none">
              Raziskujte izdelke
            </Button>
          </LocalizedClientLink>
        </div>
      </div>
    </div>
  )
}
